import type { DataProvider, Identifier, RaRecord } from "ra-core";

// Satser og kost ligger i egne tabeller som bare administrator kan lese
// (Mimin 1.5, US08). Appen ser dem fortsatt som felt på posten: lesing
// fletter dem inn der raden finnes, og lagring skriver dem til sin egen
// tabell. For en medarbeider gir RLS ingen rader, så feltene finnes ikke på
// posten, sendes ikke tilbake og eksisterende kost røres ikke.
type PrivateTable = {
  table: string;
  key: string;
  fields: string[];
  // sales_rates skrives av users-funksjonen, ikke av klienten.
  readOnly?: boolean;
};

export const PRIVATE_COSTS: Record<string, PrivateTable> = {
  sales: {
    table: "sales_rates",
    key: "sales_id",
    fields: ["hourly_rate", "hourly_cost"],
    readOnly: true,
  },
  products: {
    table: "product_costs",
    key: "product_id",
    fields: ["purchase_price"],
  },
  company_products: {
    table: "company_product_costs",
    key: "company_product_id",
    fields: ["cost"],
  },
};

type Row = Record<string, unknown>;

// Minste flate av supabase-js som trengs, så testene kan bruke en fake.
export type CostClient = {
  from(table: string): {
    select(columns: string): {
      in(
        column: string,
        values: Identifier[],
      ): PromiseLike<{ data: Row[] | null; error: { message: string } | null }>;
    };
    upsert(
      row: Row,
      options: { onConflict: string },
    ): PromiseLike<{ error: { message: string } | null }>;
  };
};

const ID_CHUNK = 200;

export async function mergePrivateCosts<T extends RaRecord>(
  client: CostClient,
  resource: string,
  records: T[],
): Promise<T[]> {
  const spec = PRIVATE_COSTS[resource];
  if (!spec || records.length === 0) return records;
  const ids = [...new Set(records.map((r) => r.id))];
  const byId = new Map<string, Row>();
  for (let i = 0; i < ids.length; i += ID_CHUNK) {
    const { data, error } = await client
      .from(spec.table)
      .select([spec.key, ...spec.fields].join(","))
      .in(spec.key, ids.slice(i, i + ID_CHUNK));
    // Mangler tabellen (SQL ikke kjørt), vises postene uten kost heller enn
    // at hele lista feiler.
    if (error) return records;
    for (const row of data ?? []) byId.set(String(row[spec.key]), row);
  }
  // De gamle kolonnene står tomme i overgangen (se migrasjonen); de fjernes
  // fra posten, så bare verdier fra den private tabellen vises og sendes.
  return records.map((record) => {
    const row = byId.get(String(record.id));
    const merged: Row = { ...record };
    for (const field of spec.fields) {
      if (row) merged[field] = row[field];
      else delete merged[field];
    }
    return merged as T;
  });
}

export function splitPrivateCosts(
  resource: string,
  data: Row,
): { data: Row; costs: Row | null } {
  const spec = PRIVATE_COSTS[resource];
  if (!spec) return { data, costs: null };
  const rest: Row = { ...data };
  const costs: Row = {};
  for (const field of spec.fields) {
    if (field in rest) {
      if (rest[field] !== undefined) costs[field] = rest[field];
      delete rest[field];
    }
  }
  const hasCosts = !spec.readOnly && Object.keys(costs).length > 0;
  return { data: rest, costs: hasCosts ? costs : null };
}

async function saveCosts(
  client: CostClient,
  resource: string,
  id: Identifier,
  costs: Row,
) {
  const spec = PRIVATE_COSTS[resource];
  const { error } = await client
    .from(spec.table)
    .upsert({ [spec.key]: id, ...costs }, { onConflict: spec.key });
  if (error) throw new Error(error.message);
}

export function withPrivateCosts<D extends DataProvider>(
  dataProvider: D,
  getClient: () => CostClient,
): D {
  const isPrivate = (resource: string) => resource in PRIVATE_COSTS;
  return {
    ...dataProvider,
    async getList(resource, params) {
      const result = await dataProvider.getList(resource, params);
      if (!isPrivate(resource)) return result;
      return {
        ...result,
        data: await mergePrivateCosts(getClient(), resource, result.data),
      };
    },
    async getManyReference(resource, params) {
      const result = await dataProvider.getManyReference(resource, params);
      if (!isPrivate(resource)) return result;
      return {
        ...result,
        data: await mergePrivateCosts(getClient(), resource, result.data),
      };
    },
    async getMany(resource, params) {
      const result = await dataProvider.getMany(resource, params);
      if (!isPrivate(resource)) return result;
      return {
        ...result,
        data: await mergePrivateCosts(getClient(), resource, result.data),
      };
    },
    async getOne(resource, params) {
      const result = await dataProvider.getOne(resource, params);
      if (!isPrivate(resource)) return result;
      const [data] = await mergePrivateCosts(getClient(), resource, [
        result.data,
      ]);
      return { ...result, data };
    },
    async create(resource, params) {
      if (!isPrivate(resource)) return dataProvider.create(resource, params);
      const { data, costs } = splitPrivateCosts(resource, params.data);
      const result = await dataProvider.create(resource, { ...params, data });
      if (!costs) return result;
      await saveCosts(getClient(), resource, result.data.id, costs);
      return { ...result, data: { ...result.data, ...costs } };
    },
    async update(resource, params) {
      if (!isPrivate(resource)) return dataProvider.update(resource, params);
      const { data, costs } = splitPrivateCosts(resource, params.data);
      // previousData kan bære kost; den skal ikke inn i PATCH-en.
      const previousData = params.previousData
        ? (splitPrivateCosts(resource, params.previousData).data as never)
        : params.previousData;
      const result = await dataProvider.update(resource, {
        ...params,
        data,
        previousData,
      });
      if (!costs) return result;
      await saveCosts(getClient(), resource, params.id, costs);
      return { ...result, data: { ...result.data, ...costs } };
    },
  };
}
